import { PropsWithChildren, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  FormKey,
  useLoaderData,
} from '~/app/features/crew/claim-line-items/resolution/resolution';
import Button from '~/components/button/Button';
import Submit from '~/components/button/Submit';
import Form from '~/components/Form';
import Icon from '~/components/Icon';
import MediaDisplay from '~/components/MediaDisplay';
import { ResolutionMethod } from '~/utils/constants';
import { useCurrencyFormatter } from '~/utils/formatters';
import { AdvancedExchangeMethod } from '~/utils/schemas';
import { resolveExchangeMethod } from './exchangeMethod';
import {
  useExchangeProducts,
  useExchangeSelection,
  variantsForOptions,
} from './exchangeOptions';
import { useOptionAvailability } from './useOptionAvailability';

export const computeBalance = (
  advancedExchangeMethod: AdvancedExchangeMethod,
  lineItemPrice: number,
  variantPrice: number,
  quantity: number,
) => {
  const balance = quantity * (variantPrice - lineItemPrice);

  if (
    advancedExchangeMethod === 'NO_PAYMENT_OR_REFUND' ||
    (balance <= 0 && advancedExchangeMethod === 'PAYMENT') ||
    (balance === 0 && advancedExchangeMethod === 'PAYMENT_AND_PARTIAL_REFUND')
  )
    return null;

  return {
    amount: Math.abs(balance),
    type: balance > 0 ? 'Payment' : 'Credit',
  };
};

function VariantOptionButton({
  selected = false,
  available = true,
  onClick,
  children,
}: PropsWithChildren<{
  selected?: boolean;
  available?: boolean;
  onClick?: () => void;
}>) {
  return (
    <Button
      type="button"
      disabled={!available}
      variant={selected ? 'filled' : 'outlined'}
      // size option for text?
      className={twMerge(
        'rounded-sm px-3 py-2',
        !available &&
          'cursor-not-allowed border-plain bg-plain text-on-plain hover:bg-plain hover:brightness-125',
        available && !selected && 'hover:brightness-75',
      )}
      onClick={onClick}
      aria-pressed={selected}
    >
      {children}
    </Button>
  );
}

function ExchangeBalance() {
  const [{ selectedProduct, selectedVariant }] = useExchangeSelection();

  const { advancedExchangeMethod } = useLoaderData();
  const {
    isNoAdjustmentVariantExchangeEnabled,
    draftClaimLineItem: {
      lineItem: { unitPrice: lineItemPrice, productIdFromPlatform },
      quantity,
    },
  } = useLoaderData();
  const currencyFormatter = useCurrencyFormatter();

  if (!selectedProduct || !selectedVariant?.price) return null;

  const { isNoAdjustmentExchange } = resolveExchangeMethod({
    advancedExchangeMethod,
    isNoAdjustmentVariantExchangeEnabled,
    originalProductIdFromPlatform: productIdFromPlatform,
    selectedProductIdFromPlatform: selectedProduct.idFromPlatform,
  });

  if (isNoAdjustmentExchange) return null;

  const balance = computeBalance(
    advancedExchangeMethod,
    lineItemPrice,
    selectedVariant.price,
    quantity,
  );

  if (!balance) return null;

  const balanceMessage = `You'll ${balance.type === 'Payment' ? 'pay an additional' : 'get back'}
  ${currencyFormatter(balance.amount)}`;

  return (
    <p className="mt-2">
      <span className="text-on-surface-variant">{balanceMessage}</span>
    </p>
  );
}

export default function ProductVariantSelector() {
  const submitRef = useRef<HTMLButtonElement>(null);
  const exchangeProducts = useExchangeProducts();
  const {
    advancedExchangeMethod,
    isNoAdjustmentVariantExchangeEnabled,
    aiExchangeRecommendation,
    draftClaimLineItem: {
      lineItem: { productIdFromPlatform },
    },
  } = useLoaderData();

  const [{ selectedProduct, selectedVariant, selectedOptions }, dispatch] =
    useExchangeSelection();
  const optionAvailability = useOptionAvailability();

  if (!selectedProduct) return null;

  const showRecommended =
    aiExchangeRecommendation &&
    aiExchangeRecommendation.variant.idFromPlatform ===
      selectedVariant?.idFromPlatform;

  const { exchangeMethod } = resolveExchangeMethod({
    advancedExchangeMethod,
    isNoAdjustmentVariantExchangeEnabled,
    originalProductIdFromPlatform: productIdFromPlatform,
    selectedProductIdFromPlatform: selectedProduct.idFromPlatform,
  });

  const possibleVariant = variantsForOptions(
    selectedProduct,
    selectedOptions,
  ).find((v) => v.imgUrl);

  return (
    <>
      <div className="grid grid-cols-1 justify-items-center gap-8 md:grid-cols-2 md:justify-items-stretch">
        <div
          data-component="corso-product-image"
          className="size-72 rounded-lg"
        >
          <MediaDisplay
            type="image"
            className="rounded-[inherit]"
            expandable
            alt={selectedVariant?.titleWithOptions || selectedProduct.name}
            src={
              selectedVariant?.imgUrl ||
              possibleVariant?.imgUrl ||
              selectedProduct.imageUrl
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-left subheading-md text-on-surface">
            {selectedProduct.name}
          </p>
          {!selectedProduct.hasOnlyDefaultVariant &&
            selectedProduct.options.map((option) => (
              <fieldset key={option.name} className="space-y-2">
                <legend className="text-left label-lg text-on-surface">
                  {option.name}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {option.values.map((value) => {
                    const { available, selected } = optionAvailability({
                      name: option.name,
                      value,
                    });
                    return (
                      <VariantOptionButton
                        key={value}
                        available={available}
                        selected={selected}
                        onClick={() =>
                          dispatch({
                            type: 'toggleOption',
                            option: { name: option.name, value },
                          })
                        }
                      >
                        {value}
                      </VariantOptionButton>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          {showRecommended && (
            <div className="mt-2 flex items-start gap-2">
              <Icon icon="SparklesSolid" className="size-5 text-brand" />
              <span className="subheading-sm text-on-surface">
                {aiExchangeRecommendation.customerRecommendationMessage ??
                  'Picked just for you'}
              </span>
            </div>
          )}
        </div>
      </div>

      <Form
        id="variantExchange"
        className="gap-4"
        name="variantExchange"
        data-testid="variantExchangeForm"
      >
        <input
          type="hidden"
          hidden
          name={FormKey.resolution}
          data-testid="exchangeResolution"
          defaultValue={ResolutionMethod.variantExchange}
        />

        <input
          type="hidden"
          hidden
          name={FormKey.advancedExchangeMethod}
          data-testid="advancedExchangeMethod"
          defaultValue={exchangeMethod}
        />

        <input
          type="hidden"
          hidden
          name={FormKey.variant}
          data-testid={FormKey.variant}
          defaultValue={JSON.stringify(selectedVariant)}
        />

        {aiExchangeRecommendation && (
          <input
            type="hidden"
            hidden
            name={FormKey.exchangeRecommendation}
            defaultValue={JSON.stringify(aiExchangeRecommendation)}
          />
        )}

        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Submit
              form="variantExchange"
              disabled={!selectedVariant}
              ref={submitRef}
            >
              Exchange
            </Submit>
            {exchangeProducts.length > 1 && (
              <Button
                variant="outlined"
                onClick={() => dispatch({ type: 'setProduct', product: null })}
              >
                Browse all products
              </Button>
            )}
          </div>
          <ExchangeBalance />
        </div>
      </Form>
    </>
  );
}
